import { useEffect, useState } from "react";
import { Card, Space, Button, notification, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import ImageWithSpinner from "../content/imagewithspinner";
import EditorCard from "../content/editorCard";
import ItemSelectionDrawer from "../content/itemselectiondrawer";
import { 
  getMenuItemsForAddOnGroup, 
  addMenuItemAddOnGroup, 
  removeMenuItemAddOnGroup 
} from "../../services/menuaddonservice";
import { useAuth } from "../../auth/authcontext";

interface MenuItem {
  guid: string;
  menuGuid?: string;
  name: string;
  imageName?: string;
}

interface MenuItemsWithAddOnGroupProps {
  selectedMaogGuid: string;
}

const MenuItemsWithAddOnGroup: React.FC<MenuItemsWithAddOnGroupProps> = ({ selectedMaogGuid }) => {
  const [menuItemsForGroup, setMenuItemsForGroup] = useState<MenuItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { userId: userGuid, isReadOnly } = useAuth();
  
  const bindMenuItemsForGroup = async (id: string) => {
     if (!id) return;
 
     try {
       const data = await getMenuItemsForAddOnGroup(id);
 
       if (Array.isArray(data)) {
         const formatted: MenuItem[] = data.map((item: any) => ({
           guid: item.guid,
           menuGuid: item.menuGuid,
           name: item.name,
           imageName: item.imageName,
         }));
 
         setMenuItemsForGroup(formatted);
       } else {
         setMenuItemsForGroup([]);
       }
     } catch (error) {
       console.error(
         "Error fetching items for add-on group",
         error
       );
       setMenuItemsForGroup([]);
     }
   };
 

  useEffect(() => {
    bindMenuItemsForGroup(selectedMaogGuid);
  }, [selectedMaogGuid]);

  const handleAddMenuItem = async (menuItemGuid: string) => {
    try {
      await addMenuItemAddOnGroup({
        menuItemId: menuItemGuid,
        menuAddOnGroupGuid: selectedMaogGuid,
        createdByGuid: userGuid ?? "",
      });
      notification.success({
        message: "Success",
        description: "Menu Item assigned successfully",
      });
      await bindMenuItemsForGroup(selectedMaogGuid);
    } catch (error: any) {
      console.error("Error assigning item to add-on group", error);
      notification.error({
        message: "Error",
        description: error?.response?.data?.message || "Failed to assign menu item to group",
      });
    }
  };

  const handleRemoveMenuItem = async (menuItemGuid: string) => {
    try {
      await removeMenuItemAddOnGroup({
        menuItemId: menuItemGuid,
        menuAddOnGroupGuid: selectedMaogGuid,
      });
      notification.success({
        message: "Success",
        description: "Menu Item unassigned successfully",
      });
      await bindMenuItemsForGroup(selectedMaogGuid);
    } catch (error) {
      console.error("Error unassigning item from add-on group", error);
      notification.error({
        message: "Error",
        description: "Failed to unassign menu item from group",
      });
    }
  };

  const MenuItemsForGroup = () => {
    if (menuItemsForGroup?.length > 0) {
      return (
        <>
          {menuItemsForGroup.map((option) => (
            <Card
              key={option.guid}
              size="small"
              style={{
                marginBottom: "8px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "4px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
              }}
              styles={{
                body: {
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  padding: "8px 12px",
                }
              }}
            >
              <Space>
                <div style={{ marginRight: "10px", width: "50px", height: "50px" }}>
                  {option.imageName && <ImageWithSpinner imageUrl={option.imageName} />}
                </div>
                <div>{option.name}</div>
              </Space>
              {!isReadOnly && (
                <Popconfirm
                  title="Are you sure you want to remove this item from the group?"
                  onConfirm={() => handleRemoveMenuItem(option.menuGuid!)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ marginLeft: "auto" }}
                  />
                </Popconfirm>
              )}
            </Card>
          ))}
        </>
      );
    } else {
      return <>No Items are assigned to this Add-On group</>;
    }
  };

  const ReturnValues = () => {
    if (selectedMaogGuid !== "") {
      return (
        <>
          <EditorCard 
            title="Items Assigned to this Group"
            extra={
              <Button 
                color="green"
                variant="solid"
                size="small"
                onClick={() => setIsDrawerOpen(true)}
              >
                Add
              </Button>
            }
          >
            <MenuItemsForGroup />
          </EditorCard>
          <ItemSelectionDrawer
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onAssign={handleAddMenuItem}
          />
        </>
      );
    } else {
      return <></>;
    }
  };

  return <ReturnValues />;
};

export default MenuItemsWithAddOnGroup;